(function () {
  if (window.__cartConditionsInitialized) return;
  window.__cartConditionsInitialized = true;

  const RULES = Object.freeze({
    servicePlanHandle: 'serviceplan-plus-1-jahr',
    requiredTagForServicePlan: 'electronic-product',

    bundleMainHandle: 'selbstreinigendes-katzenklo-neptune',
    bundleDependentHandle: 'neptune-abfallbeutel-rabatt',

    // Bundle-child tag convention: a tag like `requires-cat-toilet` means
    // the item only stays in cart while a `cat-toilet`-tagged item is also
    // present. Add new bundle families by tagging products only — no code change.
    bundleChildTagPrefix: 'requires-',
  });

  const state = {
    validating: false,
    queued: false,
    lastRunAt: 0,
  };

  const now = () => Date.now();

  const getCart = async () => {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Failed to fetch cart.js (${res.status})`);
    return await res.json();
  };

  // Shopify's /cart.js line items don't include `tags`. The cart-drawer and
  // main-cart sections render a {handle: [tags]} map into a script tag that
  // gets refreshed on every bundled-sections re-render — read from there.
  const getProductTagsFromDOM = () => {
    const el = document.querySelector('script[data-cart-product-tags]');
    if (!el) return {};
    try {
      return JSON.parse((el.textContent || '').trim() || '{}') || {};
    } catch (_) {
      return {};
    }
  };

  const enrichItemsWithTags = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;
    const tagMap = getProductTagsFromDOM();
    return {
      ...cart,
      items: cart.items.map((item) => {
        const existing = Array.isArray(item?.tags) ? item.tags : null;
        if (existing && existing.length) return item;
        return { ...item, tags: tagMap[item?.handle] || [] };
      }),
    };
  };

  const getBundledSections = () => {
    const sections = [];
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
      );
    } catch (_) {
      // If CustomEvent/detail isn't supported (very old browsers), we just don't bundle sections.
    }
    return sections;
  };

  const publishCartUpdate = (cart, meta = {}) => {
    try {
      if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-conditions',
          cart,
          ...meta,
        });
      }
    } catch (_) {
      // no-op
    }

    try {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart },
        })
      );
    } catch (_) {
      // no-op
    }
  };

  const changeQuantity = async (key, quantity) => {
    const sections = getBundledSections();
    const body = JSON.stringify({ id: key, quantity, sections });

    const url =
      (window.theme && window.theme.routes && window.theme.routes.cart_change_url) ||
      '/cart/change.js';

    const res = await fetch(url, {
      method: 'POST',
      // Both headers required: without `Accept: application/json`, Shopify
      // returns the cart page HTML instead of JSON.
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body,
    });

    if (!res.ok) throw new Error(`Failed to change item (${res.status})`);
    const cart = await res.json();

    publishCartUpdate(cart, { changedKey: key, quantity });
    return cart;
  };

  const removeByKey = (key) => changeQuantity(key, 0);

  const findServicePlanToRemove = (cart) => {
    let servicePlanItem = null;
    let hasElectronicProduct = false;

    (cart?.items || []).forEach((item) => {
      if (!item) return;

      if (item.handle === RULES.servicePlanHandle) {
        servicePlanItem = item;
        return;
      }

      if (Array.isArray(item.tags) && item.tags.includes(RULES.requiredTagForServicePlan)) {
        hasElectronicProduct = true;
      }
    });

    if (servicePlanItem && !hasElectronicProduct) {
      return servicePlanItem;
    }

    return null;
  };

  // Returns a list of { item, newQuantity } adjustments needed to bring the
  // bundle dependent's total quantity down to match the main product's.
  // Covers both: main missing entirely (dependents → 0) and main qty < dependent qty.
  const findBundleQuantityAdjustments = (cart) => {
    let mainQty = 0;
    const dependentItems = [];

    (cart?.items || []).forEach((item) => {
      if (!item) return;
      if (item.handle === RULES.bundleMainHandle) mainQty += item.quantity || 0;
      if (item.handle === RULES.bundleDependentHandle) dependentItems.push(item);
    });

    const dependentQty = dependentItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
    if (dependentQty <= mainQty) return [];

    let excess = dependentQty - mainQty;
    const adjustments = [];
    for (const item of dependentItems) {
      if (excess <= 0) break;
      const removeQty = Math.min(item.quantity || 0, excess);
      adjustments.push({ item, newQuantity: (item.quantity || 0) - removeQty });
      excess -= removeQty;
    }
    return adjustments;
  };

  // Bundle-child rule: an item tagged `requires-X` stays only while another
  // item tagged `X` is in the cart. Multiple `requires-*` tags on one item
  // mean ANY satisfying keeper is enough.
  const findOrphanedBundleChildren = (cart) => {
    const items = cart?.items || [];
    const prefix = RULES.bundleChildTagPrefix;

    // Every tag present in the cart is a candidate keeper. We don't filter
    // out `requires-*` from the keeper set because the literal-prefix check
    // below means a `requires-cat-toilet` tag never satisfies its own demand
    // (the demanded keeper is `cat-toilet`, not `requires-cat-toilet`).
    const presentTags = new Set();
    items.forEach((item) => {
      const tags = Array.isArray(item?.tags) ? item.tags : [];
      tags.forEach((t) => presentTags.add(t));
    });

    const orphans = [];
    items.forEach((item) => {
      const tags = Array.isArray(item?.tags) ? item.tags : [];
      const requiredKeepers = tags
        .filter((t) => typeof t === 'string' && t.indexOf(prefix) === 0)
        .map((t) => t.slice(prefix.length))
        .filter(Boolean);
      if (requiredKeepers.length === 0) return;
      const satisfied = requiredKeepers.some((keeper) => presentTags.has(keeper));
      if (!satisfied) orphans.push(item);
    });

    return orphans;
  };

  const validate = async (cartArg) => {
    if (state.validating) {
      state.queued = true;
      return;
    }

    // Prevent event storms from causing tight loops.
    if (now() - state.lastRunAt < 200) {
      state.queued = true;
      return;
    }

    state.validating = true;
    state.lastRunAt = now();

    try {
      let cart = cartArg;
      if (!cart || !cart.items) {
        cart = await getCart();
      }

      // Try to remove an item; on failure (race with another validator that already
      // removed it, etc.) log and keep going so subsequent rules still run.
      const tryRemove = async (item, ruleName) => {
        if (!item?.key) return;
        try {
          cart = await removeByKey(item.key);
        } catch (err) {
          console.warn(`[cart-conditions] ${ruleName} removal failed:`, item.handle, err);
          // Stale `cart` may now be wrong — refresh to keep later rules accurate.
          try {
            cart = await getCart();
          } catch (_) {
            // ignore
          }
        }
      };

      // Rule 1: service plan must be paired with electronic-product.
      const servicePlanToRemove = findServicePlanToRemove(enrichItemsWithTags(cart));
      if (servicePlanToRemove) {
        await tryRemove(servicePlanToRemove, 'service-plan');
      }

      // Rule 2: bundle dependent quantity must not exceed main quantity (1:1).
      // When the main is missing entirely (qty 0), this reduces the dependent to 0.
      const tryAdjust = async (adj, ruleName) => {
        if (!adj?.item?.key) return;
        try {
          cart = await changeQuantity(adj.item.key, adj.newQuantity);
        } catch (err) {
          console.warn(`[cart-conditions] ${ruleName} adjustment failed:`, adj.item.handle, err);
          try { cart = await getCart(); } catch (_) {}
        }
      };

      const bundleAdjustments = findBundleQuantityAdjustments(cart);
      for (const adj of bundleAdjustments) {
        await tryAdjust(adj, 'bundle-quantity');
      }

      // Rule 3: any item tagged `requires-X` needs an `X`-tagged keeper.
      const orphanedChildren = findOrphanedBundleChildren(enrichItemsWithTags(cart));
      for (const item of orphanedChildren) {
        await tryRemove(item, 'bundle-child');
      }
    } catch (err) {
      // Intentionally quiet: we don't want validation errors to break cart UX.
      console.error('[cart-conditions] validation error:', err);
    } finally {
      state.validating = false;

      if (state.queued) {
        state.queued = false;
        // Schedule the next run to allow DOM updates + cart events to settle.
        setTimeout(() => validate(), 250);
      }
    }
  };

  // Run on load.
  document.addEventListener('DOMContentLoaded', () => validate());

  // Run when theme announces a cart update (cart page + drawer emit this).
  document.addEventListener('cart:updated', (event) => {
    validate(event?.detail?.cart);
  });

  // Run on drawer refresh triggers (some code dispatches this without cart payload).
  document.addEventListener('cart:refresh', () => {
    validate();
  });
})();

